import Consts from '@/api/Consts';
import Utils from '@/api/Utils';


const Z_INDEX = {
	// Geo fencing circle layers
	geoFencingCircleOuter: 1,
	geoFencingCircleMiddle: 2,
	geoFencingCircleInner: 3,

	// Base station circle layers
	baseStationCircle: 5,
	vrsStationCircle: 6,

	// Baseline link layers
	baselineLinkBackground: 10,
	baselineLinkWhite: 11,
	baselineLinkColor: 12,
	baselineLinkArrow: 13,

	// Getter layers
	activeGetterBackground: 14,
	activeGetterMain: 15,
	failedGetter: 15,

	// Base station gradient scheme layers
	baseStationGradientBackground: 21,
	baseStationGradientMain: 22,
	baseStationGradientBackgroundFocused: 23,
	baseStationGradientMainFocused: 24,

	// Text labels
	textLabel: 31,
	textLabelGradientStyle: 32,
	textLabelFocused: 33,
	textLabelGradientStyleFocused: 34,

	baseStationSpoofingOverlay: 205,

	// Base station layers (calculated)
	baseStationBackground: 201, // 200 + 1
	baseStationMain: 202, // 200 + 2
	baseStationBackgroundFocused: 203, // 200 + 3
	baseStationMainFocused: 204, // 200 + 4
	vrsStationBackground: 101, // 100 + 1
	vrsStationMain: 102, // 100 + 2

	// Anchor point
	anchorPoint: 210,

	// Cnet constructor gridpoint layers
	cnetConstructorGridpointBackground: 220,
	cnetConstructorGridpointMain: 220,

	// Subnetwork circles (calculated dynamically)
	subnetworkCircleBase: 0,

	// Rover history fragment layers
	roverHistoryFragmentNormalEnd: 300,
	roverHistoryFragmentBackground: 301,
	roverHistoryFragmentMain: 302,
	roverHistoryFragmentBreakEnd: 303,
	roverHistoryCurrentBackground: 304,
	roverHistoryCurrentMain: 305,

	// coverage mode
	qualityRectangle: 151,
	coverageContour: 152,
} as const satisfies Record<string, number>;


const STATUS_COLORS = {
	default: 'dark0',
	success: 'successDark',
	processing: 'processingDark',
	error: 'errorDark',
	warning: 'warningDark',
	brown: 'brown',
} as const satisfies Record<string, string>;


const NEAR_SERVICE = 'near';
const VRS_SERVICE = 'vrs';


const SPRING_COLORS = [
	'#2a985f',
	'#42a65a',
	'#5cb354',
	'#77bf4b',
	'#95cb41',
	'#b5d537',
	'#d8de2d',
	'#fce627',
] as const satisfies readonly string[];

const RADIUS_MIN = 10;
const RADIUS_MAX = 20;

const SNR_MIN = 20;
const SNR_MAX = 60;
const SNR_PRECISION = 0;

const MULTIPATH_MIN = 0.1;
const MULTIPATH_MAX = 1.0;
const MULTIPATH_PRECISION = 2;

const RESIDUALS_MIN = 0;
const RESIDUALS_MAX = 50;
const RESIDUALS_PRECISION = 0;

const BASE_STATION_SPOOFING_OVERLAY_RADIUS_PX = 21;
const BASE_STATION_SPOOFING_OVERLAY_PATTERN_SIZE_PX = 8;
const BASE_STATION_SPOOFING_OVERLAY_WASH = 'rgba(80, 80, 80, 0.22)';
const BASE_STATION_SPOOFING_OVERLAY_STRIPE = 'rgba(80, 80, 80, 0.55)';
const GSS_HATCH_FILL_FALLBACK = 'rgba(80, 80, 80, 0.28)';

const PAAS_STDDEV_MIN = 10;
const PAAS_STDDEV_MAX = 2;
const paasStddevColorConverter = Utils.linearDependence(PAAS_STDDEV_MIN, PAAS_STDDEV_MAX, SPRING_COLORS.length, 0);
const paasStddevRadiusConverter = Utils.linearDependence(PAAS_STDDEV_MIN, PAAS_STDDEV_MAX, RADIUS_MAX, RADIUS_MIN);

const QUALITY_COLOR_MODE = {
	vrsPst: 'vrs_pst',
	qEff: 'q_eff',
} as const;

const QUALITY_COLOR_Q_EFF_MIN_MAX = 20;


type GpsFixQualityItem = {
	code: number;
	color: string;
};

const GPS_FIX_QUALITY_DISPLAY = [
	{ code: 0, color: Consts.CHART_COLORS[13] }, // No fix
	{ code: 1, color: Consts.HEAT_MAP_COLORS.blue }, // GPS fix
	{ code: 2, color: Consts.CHART_COLORS[5] }, // Differential GPS fix
	{ code: 3, color: Consts.CHART_COLORS[8] }, // PPS fix
	{ code: 4, color: '2e7d32' }, // RTK fixed
	{ code: 5, color: '6da488' }, // RTK float
	{ code: 6, color: Consts.CHART_COLORS[2] }, // Dead reckoning
	{ code: 7, color: Consts.CHART_COLORS[10] }, // Manual input
	{ code: 8, color: Consts.CHART_COLORS[17] }, // Simulator mode
	{ code: 9, color: 'b39437' }, // Mixed quality
] as const satisfies readonly GpsFixQualityItem[];


// маршруты, для которых нужно фокусировать внимание на карте
// убирается page container
const MAP_FOCUSED_ROUTES = [
	'home-map',
	'bs-on-map',
	'cnet-constructor-add',
	'cnet-constructor-edit',
] as const satisfies readonly string[];

type MapFocusedRoute = typeof MAP_FOCUSED_ROUTES[number];

const isMapFocusedRoute = (name: unknown): name is MapFocusedRoute => (
	typeof name === 'string' && (MAP_FOCUSED_ROUTES as readonly string[]).includes(name)
);


// маршруты, для которых используется page container, и карта
// page container уменьшается
const MAP_PARTIAL_ROUTES = [
	'rovers',
] as const satisfies readonly string[];

type MapPartialRoute = typeof MAP_PARTIAL_ROUTES[number];

const isMapPartialRoute = (name: unknown): name is MapPartialRoute => (
	typeof name === 'string' && (MAP_PARTIAL_ROUTES as readonly string[]).includes(name)
);


type QualityColorMode = typeof QUALITY_COLOR_MODE[keyof typeof QUALITY_COLOR_MODE];


export type {
	GpsFixQualityItem,
	MapFocusedRoute,
	MapPartialRoute,
	QualityColorMode,
};

export default {
	Z_INDEX,
	STATUS_COLORS,
	NEAR_SERVICE,
	VRS_SERVICE,
	SPRING_COLORS,
	RADIUS_MIN,
	RADIUS_MAX,
	SNR_MIN,
	SNR_MAX,
	SNR_PRECISION,
	MULTIPATH_MIN,
	MULTIPATH_MAX,
	MULTIPATH_PRECISION,
	RESIDUALS_MIN,
	RESIDUALS_MAX,
	RESIDUALS_PRECISION,
	BASE_STATION_SPOOFING_OVERLAY_RADIUS_PX,
	BASE_STATION_SPOOFING_OVERLAY_PATTERN_SIZE_PX,
	BASE_STATION_SPOOFING_OVERLAY_WASH,
	BASE_STATION_SPOOFING_OVERLAY_STRIPE,
	GSS_HATCH_FILL_FALLBACK,
	PAAS_STDDEV_MIN,
	PAAS_STDDEV_MAX,
	paasStddevColorConverter,
	paasStddevRadiusConverter,
	QUALITY_COLOR_MODE,
	QUALITY_COLOR_Q_EFF_MIN_MAX,
	GPS_FIX_QUALITY_DISPLAY,
	MAP_FOCUSED_ROUTES,
	MAP_PARTIAL_ROUTES,
	isMapFocusedRoute,
	isMapPartialRoute,
};
